/**
 * 💳 PAYMENT SUCCESS PAGE
 * Page de confirmation après un paiement réussi
 * Détecte le domaine pour rediriger vers la bonne app
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Crown, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// 🔧 FIX: Utiliser l'origine courante pour heartbeats.fr pour éviter les problèmes de config
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // Si on est sur heartbeats.fr, utiliser l'origine directement
  if (hostname === 'heartbeats.fr' || hostname === 'www.heartbeats.fr') {
    return window.location.origin;
  }
  // Sinon utiliser la variable d'environnement
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const API_URL = getApiUrl();

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  
  // Détecter si on est sur Heartbeats.fr
  const hostname = window.location.hostname;
  const isHeartbeatsDomain = hostname === 'heartbeats.fr' || hostname === 'www.heartbeats.fr';
  
  // Rediriger vers la bonne page selon le domaine
  const handleContinue = () => {
    if (isHeartbeatsDomain) {
      navigate('/heartbeats-portal');
    } else {
      navigate('/client');
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
        const data = await res.json();

        if (data.payment_status === 'paid') {
          setPaymentData(data);
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling (max 5 attempts)
        if (pollCount < 5) {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-16 h-16 animate-spin text-purple-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Vérification du paiement...</h2>
          <p className="text-white/60 mt-2">Merci de patienter</p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/5 backdrop-blur-xl rounded-3xl p-8 text-center border border-white/10"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="relative mb-6"
          >
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center">
              <CheckCircle className="w-14 h-14 text-white" />
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-0"
            >
              <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-2 right-4" />
              <Crown className="w-5 h-5 text-purple-400 absolute bottom-0 -left-2" />
            </motion.div>
          </motion.div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">
            Paiement réussi ! 🎉
          </h1>
          <p className="text-white/60 mb-6">
            Merci pour votre achat
          </p>

          {/* Payment Details */}
          {paymentData && (
            <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/60">Montant</span>
                <span className="text-white font-semibold">
                  {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </span>
              </div>
              {paymentData.metadata?.package_name && (
                <div className="flex justify-between items-center">
                  <span className="text-white/60">Article</span>
                  <span className="text-white">{paymentData.metadata.package_name}</span>
                </div>
              )}
            </div>
          )}

          {/* CTA Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleContinue}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
          >
            Continuer
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Error/Expired state
  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/5 backdrop-blur-xl rounded-3xl p-8 text-center border border-white/10">
        <div className="w-24 h-24 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-6">
          <span className="text-5xl">😕</span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {status === 'expired' ? 'Session expirée' : 'Une erreur est survenue'}
        </h1>
        <p className="text-white/60 mb-6">
          {status === 'expired' 
            ? 'Votre session de paiement a expiré. Veuillez réessayer.'
            : 'Impossible de vérifier le paiement. Contactez le support si vous avez été débité.'}
        </p>
        <button
          onClick={handleContinue}
          className="w-full py-4 bg-white/10 hover:bg-white/20 rounded-xl font-semibold text-white transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
